import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useNavigate, useParams } from "react-router-dom";
import LoginPage from "./components/LoginPage";
import AlbumsPage from "./components/AlbumsPage";
import AlbumPage from "./components/AlbumPage";
import SharedAlbumPage from "./components/SharedAlbumPage";
import PrivacyPolicyPage from "./components/PrivacyPolicyPage";
import DataDeletionPage from "./components/DataDeletionPage";
import { LoadingSpinner, ErrorBoundary } from "./components/shared";
import UploadToast from "./components/shared/UploadToast";
import PwaUpdateToast from "./components/shared/PwaUpdateToast";
import { useAuth } from "./contexts";
import { logError } from "./utils/errorHandler";
import ThirdParty from "supertokens-auth-react/recipe/thirdparty";
import Session from "supertokens-auth-react/recipe/session";
import { enableIosStandaloneRubberBandPrevention } from "./utils/iosStandaloneRubberBand";

function bounceToNativeApp(provider: string) {
  const appUrl = `joi.pho://auth/callback/${encodeURIComponent(provider)}${window.location.search}`;
  window.location.replace(appUrl);
}

/** Native-app-only OAuth landing — never creates a web SuperTokens session. */
function AppOAuthCallback() {
  const { provider: providerParam } = useParams<{ provider: string }>();
  const provider = providerParam || "google";

  React.useEffect(() => {
    bounceToNativeApp(provider);
  }, [provider]);

  return (
    <div
      data-section="app-root"
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        flexDirection: "column",
        gap: 12,
        padding: 24,
        textAlign: "center",
      }}
    >
      <LoadingSpinner message="Opening the joint.photos app…" size="large" />
      <p style={{ color: "#94a3b8", maxWidth: 360 }}>If nothing happens, close this window and return to the app.</p>
    </div>
  );
}

function ThirdPartyCallback() {
  const navigate = useNavigate();
  const { refreshAuth } = useAuth();

  React.useEffect(() => {
    let cancelled = false;

    const completeWebSignIn = async () => {
      if (cancelled) {
        return;
      }
      try {
        const response = await ThirdParty.signInAndUp();
        if (cancelled) {
          return;
        }
        if (response.status === "OK") {
          const hasSession = await Session.doesSessionExist();
          if (!hasSession) {
            await Session.attemptRefreshingSession();
          }
          await refreshAuth();
          navigate("/", { replace: true });
          return;
        }
        navigate("/", { replace: true });
      } catch (e) {
        console.error("ThirdParty callback error", e);
        if (!cancelled) {
          navigate("/", { replace: true });
        }
      }
    };

    // Web / PWA callback. Native OAuth uses /auth/app-callback/* so we never
    // bounce joi.pho:// here — that scheme is invalid when the app is missing.
    void completeWebSignIn();
    return () => {
      cancelled = true;
    };
  }, [navigate, refreshAuth]);

  return (
    <div
      data-section="app-root"
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        flexDirection: "column",
        gap: 12,
        padding: 24,
        textAlign: "center",
      }}
    >
      <LoadingSpinner message="Signing you in…" size="large" />
    </div>
  );
}

function AppContent() {
  const { isAuthenticated, isLoading, signOut } = useAuth();
  const pathname = window.location.pathname;
  const isAuthCallback = pathname.startsWith("/auth/callback/") || pathname.startsWith("/auth/app-callback/");

  if (isAuthCallback) {
    return (
      <div data-section="app-root" className="pwa-scroll-container">
        <ErrorBoundary onError={logError}>
          <Routes>
            <Route path="/auth/app-callback/:provider" element={<AppOAuthCallback />} />
            <Route path="/auth/callback/:provider" element={<ThirdPartyCallback />} />
          </Routes>
        </ErrorBoundary>
      </div>
    );
  }

  return (
    <div data-section="app-root" className="pwa-scroll-container">
      <ErrorBoundary onError={logError}>
        {isLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100vh",
            }}
          >
            <LoadingSpinner message="Loading..." size="large" />
          </div>
        ) : (
          <Routes>
            <Route path="/auth/app-callback/:provider" element={<AppOAuthCallback />} />
            <Route path="/auth/callback/:provider" element={<ThirdPartyCallback />} />

            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/data-deletion" element={<DataDeletionPage />} />

            <Route path="/owners/:ownerId/albums/:albumId/link" element={<SharedAlbumPage />} />

            <Route path="/" element={isAuthenticated ? <AlbumsPage onLogout={signOut} /> : <LoginPage />} />
            <Route path="/owners/:ownerId/albums/:albumId" element={isAuthenticated ? <AlbumPage /> : <LoginPage />} />
          </Routes>
        )}
      </ErrorBoundary>
      <UploadToast />
    </div>
  );
}

function App() {
  React.useEffect(() => {
    const cleanup = enableIosStandaloneRubberBandPrevention();
    return cleanup;
  }, []);

  return (
    <BrowserRouter>
      <AppContent />
      <PwaUpdateToast />
    </BrowserRouter>
  );
}

export default App;
